import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export type CompanyApplication = {
  id: string;
  status: 'pending' | 'approved' | 'rejected';
  company_name: string;
  org_number: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  company_phone: string | null;
  company_email: string | null;
  owner_full_name: string;
  owner_email: string;
  owner_phone: string | null;
  fleet_size: number | null;
  notes: string | null;
  reject_reason: string | null;
  company_id: string | null;
  created_at: string;
};

export function useCompanyApplications(status: 'pending' | 'all' = 'pending') {
  const [applications, setApplications] = useState<CompanyApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    let query = supabase
      .from('company_applications')
      .select('*')
      .order('created_at', { ascending: false });

    if (status === 'pending') {
      query = query.eq('status', 'pending');
    }

    const { data, error: err } = await query;
    if (err) {
      setError(err.message);
      setApplications([]);
    } else {
      setApplications((data as CompanyApplication[]) ?? []);
    }
    setLoading(false);
  }, [status]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  async function approve(id: string, sendEmail = true) {
    const { data: fnData, error: fnErr } = await supabase.functions.invoke('approve-company-application', {
      body: { application_id: id, send_email: sendEmail },
    });

    if (!fnErr && fnData && !(fnData as { error?: string }).error) {
      const payload = fnData as { error?: string; password?: string; owner_email?: string };
      await refetch();
      return { data: payload, error: null };
    }

    // Fallback when Edge Function not deployed (CORS / preflight failure)
    const { data: rpcData, error: rpcErr } = await supabase.rpc('approve_company_application_dashboard', {
      p_application_id: id,
      p_temp_password: 'Vilotid123!',
    });

    if (rpcErr) {
      const edgeMsg = fnErr?.message ?? '';
      return {
        data: null,
        error: rpcErr.message || edgeMsg || 'Approval failed — apply migration 022 in Supabase SQL Editor',
      };
    }

    const payload = rpcData as { owner_email?: string; password?: string; company_name?: string };
    await refetch();
    return { data: payload, error: null };
  }

  async function reject(id: string, reason?: string) {
    const { data: fnData, error: fnErr } = await supabase.functions.invoke('reject-company-application', {
      body: { application_id: id, reason },
    });

    if (!fnErr && fnData && !(fnData as { error?: string }).error) {
      await refetch();
      return { error: null };
    }

    const { error: rpcErr } = await supabase.rpc('reject_company_application_dashboard', {
      p_application_id: id,
      p_reason: reason ?? null,
    });

    if (rpcErr) {
      return { error: rpcErr.message || fnErr?.message || 'Reject failed — apply migration 022' };
    }
    await refetch();
    return { error: null };
  }

  return { applications, loading, error, refetch, approve, reject };
}

export async function submitCompanyApplication(input: {
  company_name: string;
  org_number?: string;
  address?: string;
  postal_code?: string;
  city?: string;
  company_phone?: string;
  company_email?: string;
  owner_full_name: string;
  owner_email: string;
  owner_phone?: string;
  fleet_size?: number;
  notes?: string;
}) {
  const { data, error } = await supabase.rpc('submit_company_application', {
    p_company_name: input.company_name,
    p_org_number: input.org_number ?? null,
    p_address: input.address ?? null,
    p_postal_code: input.postal_code ?? null,
    p_city: input.city ?? null,
    p_company_phone: input.company_phone ?? null,
    p_company_email: input.company_email ?? null,
    p_owner_full_name: input.owner_full_name,
    p_owner_email: input.owner_email,
    p_owner_phone: input.owner_phone ?? null,
    p_fleet_size: input.fleet_size ?? null,
    p_notes: input.notes ?? null,
  });

  if (error) return { id: null, error: error.message };
  return { id: data as string, error: null };
}
